/* ==========================================================================
   Rook UI Core — Popover Controller
   Web Component: <rk-popover-element>
   ========================================================================== */

if (!customElements.get('rk-popover-element')) {
    class RkPopoverElement extends HTMLElement {
        constructor() {
            super();
            this.trigger = null;
            this.content = null;
            this.previousFocus = null;
            this._onClickOutside = this._onClickOutside.bind(this);
            this._onKeyDown = this._onKeyDown.bind(this);
            this._onResize = this._debounce(this._reposition.bind(this), 100);
        }

        connectedCallback() {
            this.trigger = this.querySelector('[data-rk-popover-trigger]');
            this.content = this.querySelector('.rk-popover__content');

            if (!this.trigger || !this.content) return;

            this.trigger.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.toggle();
            });

            // Ensure trigger has a11y attributes
            this.trigger.setAttribute('aria-haspopup', 'dialog');
            this.trigger.setAttribute('aria-expanded', 'false');

            // Link trigger to content
            const contentId = this.content.id || `rk-popover-${this._uid()}`;
            this.content.id = contentId;
            this.trigger.setAttribute('aria-controls', contentId);
        }

        disconnectedCallback() {
            this._removeGlobalListeners();
        }

        /* ------------------------------------------------------------------ */
        /*  Public API                                                        */
        /* ------------------------------------------------------------------ */

        toggle() {
            this.isOpen() ? this.close() : this.open();
        }

        isOpen() {
            return this.content?.classList.contains('rk-popover__content--active');
        }

        open() {
            if (!this.content || this.isOpen()) return;

            this.previousFocus = document.activeElement;
            this.content.classList.add('rk-popover__content--active');
            this.trigger?.setAttribute('aria-expanded', 'true');

            this._reposition();
            this._addGlobalListeners();

            // Trap focus — focus first interactive element inside
            requestAnimationFrame(() => {
                const firstFocusable = this.content.querySelector(
                    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
                );
                if (firstFocusable) {
                    firstFocusable.focus();
                }
            });

            this.dispatchEvent(
                new CustomEvent('rk:popover:open', { bubbles: true, detail: { id: this.content.id } })
            );
        }

        close() {
            if (!this.content || !this.isOpen()) return;

            this.content.classList.remove('rk-popover__content--active');
            this.trigger?.setAttribute('aria-expanded', 'false');

            this._removeGlobalListeners();

            // Restore focus
            if (this.previousFocus) {
                this.previousFocus.focus();
                this.previousFocus = null;
            }

            this.dispatchEvent(
                new CustomEvent('rk:popover:close', { bubbles: true, detail: { id: this.content.id } })
            );
        }

        /* ------------------------------------------------------------------ */
        /*  Positioning Engine                                                */
        /* ------------------------------------------------------------------ */

        _reposition() {
            if (!this.trigger || !this.content || !this.isOpen()) return;

            const preferredSide = this.dataset.side || 'bottom';
            const align = this.dataset.align || 'center';
            const sideOffset = parseInt(this.dataset.sideOffset, 10) || 8;
            const alignOffset = parseInt(this.dataset.alignOffset, 10) || 0;

            const triggerRect = this.trigger.getBoundingClientRect();
            const contentRect = this.content.getBoundingClientRect();
            const viewportW = window.innerWidth;
            const viewportH = window.innerHeight;

            // Determine actual side (with collision detection / auto-flip)
            const side = this._resolveSide(preferredSide, triggerRect, contentRect, viewportW, viewportH, sideOffset);

            let top = 0;
            let left = 0;

            // 1. Calculate position on the main axis (side)
            switch (side) {
                case 'top':
                    top = -(contentRect.height + sideOffset);
                    break;
                case 'bottom':
                    top = triggerRect.height + sideOffset;
                    break;
                case 'left':
                    left = -(contentRect.width + sideOffset);
                    break;
                case 'right':
                    left = triggerRect.width + sideOffset;
                    break;
            }

            // 2. Calculate position on the cross axis (align)
            if (side === 'top' || side === 'bottom') {
                switch (align) {
                    case 'start':
                        left = alignOffset;
                        break;
                    case 'center':
                        left = (triggerRect.width - contentRect.width) / 2 + alignOffset;
                        break;
                    case 'end':
                        left = triggerRect.width - contentRect.width + alignOffset;
                        break;
                }
            } else {
                switch (align) {
                    case 'start':
                        top = alignOffset;
                        break;
                    case 'center':
                        top = (triggerRect.height - contentRect.height) / 2 + alignOffset;
                        break;
                    case 'end':
                        top = triggerRect.height - contentRect.height + alignOffset;
                        break;
                }
            }

            // 3. Clamp to viewport edges (prevent overflow)
            const absLeft = triggerRect.left + left;
            const absTop = triggerRect.top + top;

            if (absLeft < 8) {
                left += (8 - absLeft);
            } else if (absLeft + contentRect.width > viewportW - 8) {
                left -= (absLeft + contentRect.width - viewportW + 8);
            }

            if (absTop < 8) {
                top += (8 - absTop);
            } else if (absTop + contentRect.height > viewportH - 8) {
                top -= (absTop + contentRect.height - viewportH + 8);
            }

            this.content.style.top = `${top}px`;
            this.content.style.left = `${left}px`;

            // Set data-side for arrow / animation CSS
            this.content.dataset.side = side;
        }

        _resolveSide(preferred, triggerRect, contentRect, vw, vh, offset) {
            const space = {
                top: triggerRect.top - offset,
                bottom: vh - triggerRect.bottom - offset,
                left: triggerRect.left - offset,
                right: vw - triggerRect.right - offset,
            };

            const fits = {
                top: space.top >= contentRect.height,
                bottom: space.bottom >= contentRect.height,
                left: space.left >= contentRect.width,
                right: space.right >= contentRect.width,
            };

            if (fits[preferred]) return preferred;

            // Flip to opposite side
            const opposite = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };
            if (fits[opposite[preferred]]) return opposite[preferred];

            // Fallback: pick side with most space
            const sorted = Object.entries(space).sort((a, b) => b[1] - a[1]);
            return sorted[0][0];
        }

        /* ------------------------------------------------------------------ */
        /*  Global Listeners                                                  */
        /* ------------------------------------------------------------------ */

        _addGlobalListeners() {
            document.addEventListener('click', this._onClickOutside, true);
            document.addEventListener('keydown', this._onKeyDown);
            window.addEventListener('resize', this._onResize);
            window.addEventListener('scroll', this._onResize, true);
        }

        _removeGlobalListeners() {
            document.removeEventListener('click', this._onClickOutside, true);
            document.removeEventListener('keydown', this._onKeyDown);
            window.removeEventListener('resize', this._onResize);
            window.removeEventListener('scroll', this._onResize, true);
        }

        _onClickOutside(e) {
            if (!this.contains(e.target)) {
                this.close();
            }
        }

        _onKeyDown(e) {
            if (e.key === 'Escape' && this.isOpen()) {
                e.preventDefault();
                this.close();
            }
        }

        /* ------------------------------------------------------------------ */
        /*  Helpers                                                           */
        /* ------------------------------------------------------------------ */

        _uid() {
            return Math.random().toString(36).substring(2, 9);
        }

        _debounce(fn, delay) {
            let timer;
            return (...args) => {
                clearTimeout(timer);
                timer = setTimeout(() => fn(...args), delay);
            };
        }
    }

    customElements.define('rk-popover-element', RkPopoverElement);
}
